import { SimulationState } from '@/types/simulation';

interface Props {
  simState: SimulationState;
  onStart: () => void;
  onPause: () => void;
  onReset: () => void;
  onUpdate: (updates: Partial<SimulationState>) => void;
  isDark?: boolean;
}

const SPEED_OPTIONS = [0.25, 0.5, 1, 2, 4];

export default function SimulationControls({
  simState,
  onStart,
  onPause,
  onReset,
  onUpdate,
  isDark = true,
}: Props) {
  const toggle = () => (simState.running ? onPause() : onStart());

  const textMuted     = isDark ? 'text-white/35'  : 'text-gray-500';
  const textSecondary = isDark ? 'text-white/60'  : 'text-gray-600';
  const textPrimary   = isDark ? 'text-white'     : 'text-gray-900';
  const textDim       = isDark ? 'text-white/18'  : 'text-gray-300';

  return (
    <div className="space-y-4 animate-float-in">

      <h2 className="section-label flex items-center gap-2">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={`ri-rocket-line ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
        </div>
        シミュレーション
      </h2>

      {/* Play / Pause / Reset */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={onReset}
          className={`glass-btn ripple-btn w-10 h-10 flex items-center justify-center rounded-full cursor-pointer ${textMuted} hover:${textPrimary} transition-all`}
          title="リセット"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-restart-line text-lg" />
          </div>
        </button>

        <button
          onClick={toggle}
          style={{ width: 68, height: 68 }}
          className={`ripple-btn relative flex items-center justify-center rounded-full cursor-pointer transition-all duration-300 ${
            simState.running
              ? 'bg-orange-500/70 border border-orange-400/50 shadow-[0_0_28px_rgba(255,107,53,0.5)] animate-pulse-glow'
              : isDark
              ? 'bg-emerald-500/60 border border-emerald-400/50 shadow-[0_0_18px_rgba(52,211,153,0.35)]'
              : 'bg-emerald-500 border border-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.25)]'
          }`}
        >
          <div className="w-8 h-8 flex items-center justify-center">
            <i className={`${simState.running ? 'ri-pause-fill' : 'ri-play-fill'} text-3xl text-white`} />
          </div>
          <span className="absolute inset-0 rounded-full border border-white/10 pointer-events-none" />
        </button>

        <button
          onClick={() => onUpdate({ showTrails: !simState.showTrails })}
          className={`ripple-btn w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all ${
            simState.showTrails
              ? `glass-btn-active ${isDark ? 'text-cyan-300' : 'text-sky-600'}`
              : `glass-btn ${textMuted} hover:${textSecondary}`
          }`}
          title="軌跡表示"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-route-line text-lg" />
          </div>
        </button>


      </div>

      {/* Button labels */}
      <div className="flex items-center justify-center gap-4">
        <span className="w-10 text-center" />
        <span className="w-[68px]" />
        <span className={`w-10 text-center text-xs ${simState.showTrails ? (isDark ? 'text-cyan-400' : 'text-sky-600') : textMuted}`}>
          軌跡
        </span>
      </div>

      {/* Speed */}
      <div>
        <p className={`text-xs ${textMuted} mb-2`}>再生速度</p>
        <div className="flex gap-1">
          {SPEED_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => onUpdate({ speed: s })}
              className={`ripple-btn flex-1 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                simState.speed === s
                  ? `glass-btn-active ${textPrimary}`
                  : `glass-btn ${textMuted} hover:${textSecondary}`
              }`}
            >
              {s}x
            </button>
          ))}
        </div>
      </div>

      {/* Gravity */}
      <div className="glass rounded-xl p-3 space-y-2">
        <div className="flex justify-between items-center">
          <span className={`text-xs ${textMuted} flex items-center gap-1.5`}>
            <div className="w-3 h-3 flex items-center justify-center">
              <i className={`ri-arrow-down-line text-xs ${textMuted}`} />
            </div>
            重力
          </span>
          <span className={`text-xs font-mono font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
            {simState.gravity.toFixed(1)} m/s²
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="30"
          step="0.1"
          value={Math.min(simState.gravity, 30)}
          onChange={(e) => onUpdate({ gravity: Number(e.target.value) })}
          className={`w-full cursor-pointer ${isDark ? 'accent-emerald-400' : 'accent-emerald-600'}`}
        />
        <div className={`flex justify-between text-xs ${textDim}`}>
          <span>無重力</span>
          <span>地球 9.8</span>
          <span>木星 24.8</span>
        </div>

        {/* Gravity presets — 横スクロール */}
        <div className="overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: 'none' }}>
          <div className="flex gap-1 pt-1" style={{ minWidth: 'max-content' }}>
            {([
              ['無重力', 0],
              ['地球', 9.8],
              ['月', 1.6],
              ['火星', 3.7],
              ['金星', 8.9],
              ['ベテルギウス', 8.0],
              ['土星', 10.4],
              ['海王星', 11.2],
              ['木星', 24.8],
              ['冥王星', 0.6],
              ['太陽', 274],
              ['白色矮星', 3000],
              ['中性子星', 200000],
              ['BH', 1e10],
            ] as [string, number][]).map(([label, val]) => (
              <button
                key={label}
                onClick={() => onUpdate({ gravity: val })}
                className={`ripple-btn px-2.5 py-1 rounded-md text-xs transition-all cursor-pointer whitespace-nowrap ${
                  Math.abs(simState.gravity - val) < (val === 0 ? 0.05 : val * 0.001 + 0.05)
                    ? `glass-btn-active ${textPrimary}`
                    : `glass-btn ${textMuted} hover:${textSecondary}`
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
